/**
 * The three decisions behind the chat composer's multi-line textarea
 * (client/src/components/Composer.jsx), kept out of the component because the client test
 * harness is jsdom WITHOUT @testing-library or a React plugin — a .jsx component cannot be
 * rendered, or even transformed, under `vitest run`. Logic that stays in the component is
 * logic that can never be tested here, so the fiddly parts live in this module.
 */

/** One line of the composer, in px. Must match the textarea's rendered line-height. */
export const COMPOSER_LINE_HEIGHT = 20;
/** Vertical padding inside the textarea (top + bottom), included in scrollHeight. */
export const COMPOSER_PADDING = 16;
export const COMPOSER_MIN_LINES = 1;
export const COMPOSER_MAX_LINES = 8;

/**
 * Whether this keystroke should send the message rather than insert a newline.
 *
 * Two of these guards are the ones that get skipped and then bite:
 *   - `isComposing` — on a Japanese/Chinese/Korean IME, Enter COMMITS the candidate word.
 *     Without this, every commit fires a half-written message.
 *   - `isTouch` — a phone keyboard's return key IS Enter and there is no Shift, so
 *     Enter-to-send would make multi-line typing impossible on mobile. There, Enter always
 *     inserts a newline and sending is the button's job.
 */
export function shouldSendOnKey(event, { isTouch = false } = {}) {
  if (!event || event.key !== 'Enter') return false;
  if (event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) return false;
  // React exposes the flag on the native event; some synthetic events carry it directly.
  if (event.isComposing || event.nativeEvent?.isComposing) return false;
  if (event.keyCode === 229) return false; // legacy IME signal, still emitted by some browsers
  return !isTouch;
}

/**
 * The height to give the textarea for a measured `scrollHeight`. Clamped at both ends: the
 * floor keeps an empty composer exactly one line tall (so it still reads as the single-line
 * pill it replaced), and the ceiling stops a pasted essay from swallowing the whole panel —
 * past it the textarea scrolls instead.
 */
/**
 * Callers pass 0 for an empty box rather than a measurement: Chrome reports an EMPTY
 * textarea's scrollHeight as two lines (54px where one line of real text measures 34px),
 * so measuring the empty case would leave the composer permanently one line too tall —
 * including right after every send.
 */
export function clampComposerHeight(scrollHeight, {
  lineHeight = COMPOSER_LINE_HEIGHT,
  padding = COMPOSER_PADDING,
  minLines = COMPOSER_MIN_LINES,
  maxLines = COMPOSER_MAX_LINES,
} = {}) {
  const min = minLines * lineHeight + padding;
  const max = maxLines * lineHeight + padding;
  const measured = Number.isFinite(scrollHeight) ? scrollHeight : min;
  return Math.min(Math.max(measured, min), max);
}

/**
 * The draft text while dictating. `committed` is what speech recognition has finalised this
 * session and `interim` is the provisional phrase still being spoken — successive interim
 * frames REPLACE each other rather than stacking, which is the whole reason this is a
 * function of (base, committed, interim) instead of an append.
 *
 * `base` is whatever the user had typed before pressing the mic; dictation extends it rather
 * than replacing it.
 */
export function composeDictation(base, { committed = '', interim = '' } = {}) {
  const parts = [base, committed, interim]
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean);
  return parts.join(' ');
}
